import { useEffect, useState } from "react"
import {
  FileText,
  CheckCircle,
  DollarSign,
  FileCheck,
  Clock,
  AlertTriangle,
} from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { fetchWithAuth } from "@/lib/api/client"

interface DashboardStats {
  total_licitaciones: number
  licitaciones_por_estado: Record<string, number>
  total_documentos: number
  total_propuestas: number
  propuestas_por_estado: Record<string, number>
  total_chat_sessions: number
  total_agent_executions: number
  tasa_exito: number
  ganadas: number
  perdidas: number
  presupuesto_total_gestionado: number
  presupuesto_ganado: number
  // Mejora E (changelog cliente): KPIs accionables.
  vencimientos_proximos_30d: number
  vencimientos_proximos_7d: number
  licitaciones_sin_propuesta: number
}

interface StatCard {
  icon: React.ElementType
  label: string
  value: string
  subtext: string
  colorClass: string
  iconBgClass: string
}

export function StatsCards() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchStats() {
      try {
        const data = await fetchWithAuth("/api/v1/analytics/dashboard")
        setStats(data)
      } catch (error) {
        console.error("Error fetching dashboard stats:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading) {
    return <StatsCardsLoading />
  }

  if (!stats) {
    return null
  }

  // Calcular licitaciones activas (estados no terminales). Ver
  // backend/app/models/licitacion.py::EstadoLicitacion para la fuente de verdad.
  const licitacionesActivas =
    (stats.licitaciones_por_estado["BORRADOR"] || 0) +
    (stats.licitaciones_por_estado["EN_ANALISIS"] || 0) +
    (stats.licitaciones_por_estado["EN_PREPARACION"] || 0) +
    (stats.licitaciones_por_estado["PRESENTADA"] || 0) +
    (stats.licitaciones_por_estado["EN_EVALUACION"] || 0)

  // Formatear presupuesto (millones)
  const formatPresupuesto = (value: number): string => {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`
    }
    return value.toString()
  }

  // Mejora E (changelog cliente): orden de KPIs accionables primero.
  // Lo urgente (vencimientos, sin propuesta) sale antes que los agregados
  // históricos (tasa de éxito, presupuesto ganado). En la reunión 2026-04-10
  // el cliente dejó claro que los KPIs hay que trabajarlos juntos, así que
  // priorizamos lo que dispara acción inmediata del equipo.
  const cards: StatCard[] = [
    {
      icon: Clock,
      label: "Vencen en 7 días",
      value: stats.vencimientos_proximos_7d.toString(),
      subtext:
        stats.vencimientos_proximos_30d > 0
          ? `${stats.vencimientos_proximos_30d} en 30 días`
          : "Sin vencimientos próximos",
      colorClass:
        stats.vencimientos_proximos_7d > 0 ? "text-red-600" : "text-gray-500",
      iconBgClass:
        stats.vencimientos_proximos_7d > 0 ? "bg-red-50" : "bg-gray-50",
    },
    {
      icon: AlertTriangle,
      label: "Sin propuesta",
      value: stats.licitaciones_sin_propuesta.toString(),
      subtext: "Activas sin borrador",
      colorClass:
        stats.licitaciones_sin_propuesta > 0
          ? "text-amber-600"
          : "text-gray-500",
      iconBgClass:
        stats.licitaciones_sin_propuesta > 0 ? "bg-amber-50" : "bg-gray-50",
    },
    {
      icon: FileCheck,
      label: "Licitaciones Activas",
      value: licitacionesActivas.toString(),
      subtext: `${stats.total_licitaciones} en total`,
      colorClass: "text-blue-600",
      iconBgClass: "bg-blue-50",
    },
    {
      icon: FileText,
      label: "Propuestas",
      value: stats.total_propuestas.toString(),
      subtext: `${stats.propuestas_por_estado["BORRADOR"] || 0} en borrador`,
      colorClass: "text-indigo-600",
      iconBgClass: "bg-indigo-50",
    },
    {
      icon: CheckCircle,
      label: "Tasa de Éxito",
      value: `${stats.tasa_exito.toFixed(0)}%`,
      subtext: `${stats.ganadas} ganadas`,
      colorClass: "text-green-600",
      iconBgClass: "bg-green-50",
    },
    {
      icon: DollarSign,
      label: "Presupuesto Ganado",
      value: `€${formatPresupuesto(stats.presupuesto_ganado)}`,
      subtext: `de €${formatPresupuesto(stats.presupuesto_total_gestionado)} total`,
      colorClass: "text-emerald-600",
      iconBgClass: "bg-emerald-50",
    },
  ]

  return (
    <>
      {cards.map((card, index) => (
        <Card key={index} className="overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={`rounded-lg p-3 ${card.iconBgClass}`}>
                <card.icon className={`h-6 w-6 ${card.colorClass}`} />
              </div>

              {/* Content */}
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-muted-foreground">
                  {card.label}
                </p>
                <p className="text-2xl font-bold tracking-tight">{card.value}</p>
                <p className="text-xs text-muted-foreground">{card.subtext}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  )
}

function StatsCardsLoading() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  )
}
